import type { NonUndefined } from 'utility-types'
import type { FC } from 'react'
import type { SvgIconProps } from '@mui/material'

import type { PartialReadonlyRecord } from '../../../utils'

import { Translation } from '../../../domain'

import {
    AlertTriangleOutlineIcon,
    CatalogOutlineIcon,
    CertificateOutlineIcon,
    ClientIcon,
    CurlyBracketsIcon,
    DataFilledIcon,
    ExplorerOutlineIcon,
    LogsIcon,
    NodeRedWorkbenchOutlineIcon,
    OverviewIcon,
    PasswordOutlineIcon,
    ResourcesOutlineIcon,
    ServiceIcon,
    SettingsIcon,
    SystemFilledIcon,
    SystemStatusIcon,
    UserSettingsIcon,
    UsersIcon,
} from '../common'
import { AbsoluteRouteOnlyPath } from '../routing'

type NavigationConfig = Readonly<{
    /**
     * Side Navigation contains two grouped menus:
     * – the main menu at the top
     * – the extra menu at the bottom
     * The flag below defines whether a nav item should be displayed in the extra menu.
     */
    isExtraMenu?: boolean

    /**
     * The translation ready description of the route
     */
    description?: Translation

    /**
     * Icon
     */
    icon?: FC<SvgIconProps>
}>

const navigationConfig: PartialReadonlyRecord<AbsoluteRouteOnlyPath, NavigationConfig> = {
    [AbsoluteRouteOnlyPath.SYSTEM]: {
        icon: SystemFilledIcon,
    },
    [AbsoluteRouteOnlyPath.DATA]: {
        icon: DataFilledIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES]: {
        icon: ServiceIcon,
    },
    [AbsoluteRouteOnlyPath.USER]: {
        icon: UsersIcon,
    },
    [AbsoluteRouteOnlyPath.SETTINGS]: {
        icon: SettingsIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES_OVERVIEW]: {
        description: Translation.NAVIGATION_SERVICES_DESCRIPTION,
        icon: OverviewIcon,
    },
    [AbsoluteRouteOnlyPath.DEVIATED_SERVICES]: {
        icon: AlertTriangleOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES_LOGS]: {
        icon: LogsIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES_WORKBENCH]: {
        isExtraMenu: true,
        icon: NodeRedWorkbenchOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES]: {
        description: Translation.NAVIGATION_MAPPINGS_DESCRIPTION,
        icon: ResourcesOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES_CATALOG]: {
        description: Translation.NAVIGATION_SERVICES_CATALOG_DESCRIPTION,
        icon: CatalogOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.SERVICES_RULE_ENGINE_SANDBOX]: {
        isExtraMenu: true,
        description: Translation.NAVIGATION_RULE_ENGINE_SANDBOX_DESCRIPTION,
        icon: CurlyBracketsIcon,
    },
    [AbsoluteRouteOnlyPath.SYSTEM_STATUS]: {
        description: Translation.NAVIGATION_SYSTEM_STATUSES_DESCRIPTION,
        icon: SystemStatusIcon,
    },
    [AbsoluteRouteOnlyPath.DATA_EXPLORER]: {
        description: Translation.NAVIGATION_EXPLORER_DESCRIPTION,
        icon: ExplorerOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.USER_MANAGEMENT]: {
        icon: UserSettingsIcon,
    },
    [AbsoluteRouteOnlyPath.USER_CLIENT_REGISTRY]: {
        description: Translation.NAVIGATION_CLIENT_REGISTRY_DESCRIPTION,
        icon: ClientIcon,
    },
    [AbsoluteRouteOnlyPath.USER_CERTIFICATES]: {
        description: Translation.NAVIGATION_CERTIFICATES_DESCRIPTION,
        icon: CertificateOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.SETTINGS_LOGIN]: {
        icon: PasswordOutlineIcon,
    },
    [AbsoluteRouteOnlyPath.SYSTEM_LICENSE]: { description: Translation.NAVIGATION_SYSTEM_LICENSE_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SYSTEM_METRICS]: { description: Translation.NAVIGATION_SYSTEM_METRICS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SYSTEM_AGENTS]: { description: Translation.NAVIGATION_SYSTEM_AGENTS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_LINKS]: { description: Translation.NAVIGATION_SERVICES_LINKS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_SERVERS]: { description: Translation.NAVIGATION_SERVERS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_NODES]: { description: Translation.NAVIGATION_NODES_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_CONTAINERS]: { description: Translation.NAVIGATION_CONTAINERS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_VOLUMES]: { description: Translation.NAVIGATION_VOLUMES_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_CONNECTIONS]: { description: Translation.NAVIGATION_CONNECTIONS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_ENDPOINTS]: { description: Translation.NAVIGATION_ENDPOINTS_DESCRIPTION },
    [AbsoluteRouteOnlyPath.SERVICES_RESOURCES_MAPPINGS]: { description: Translation.NAVIGATION_MAPPINGS_DESCRIPTION },
}

export const getNavigationConfig = <Prop extends keyof NavigationConfig>(
    path: AbsoluteRouteOnlyPath,
    prop: Prop
): NonUndefined<NavigationConfig[Prop]> | null =>
    ((navigationConfig[path]?.[prop] as NavigationConfig[Prop]) ?? null) as NonUndefined<NavigationConfig[Prop]> | null
